import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { buildTenantBackupObjects, buildTenantBackupRuns } from '../model/tenant-backups.model.js';

export function useTenantBackups() {
  const { token, hasPermission } = useAuth();
  const [backups, setBackups] = useState([]);
  const [runs, setRuns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [restoreState, setRestoreState] = useState({
    isOpen: false,
    backup: null,
    confirmDbName: '',
    error: '',
  });

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const [backupsResponse, runsResponse] = await Promise.all([
        adminApi.getTenantBackups(token),
        adminApi.getTenantBackupRuns(token),
      ]);

      setBackups(buildTenantBackupObjects(backupsResponse));
      setRuns(buildTenantBackupRuns(runsResponse));
    } catch (requestError) {
      setError(requestError?.message || 'Failed to load backups.');
      setBackups([]);
      setRuns([]);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadBackup = useCallback(async (backup) => {
    setIsActionLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.downloadTenantBackup(token, backup);
      const url = URL.createObjectURL(response.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = backup.downloadFilename || response.filename || backup.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setSuccess('Backup download started.');
    } catch (requestError) {
      setError(requestError?.message || 'Failed to download backup.');
    } finally {
      setIsActionLoading(false);
    }
  }, [token]);

  const downloadBackupFiles = useCallback(async (backup) => {
    setIsActionLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.downloadTenantBackupFiles(token, backup);
      const url = URL.createObjectURL(response.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = backup.filesDownloadFilename || response.filename || 'backup-files.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setSuccess('Backup files download started.');
    } catch (requestError) {
      setError(requestError?.message || 'Failed to download backup files.');
    } finally {
      setIsActionLoading(false);
    }
  }, [token]);

  const openRestore = useCallback((backup) => {
    setRestoreState({
      isOpen: true,
      backup,
      confirmDbName: '',
      error: '',
    });
  }, []);

  const closeRestore = useCallback(() => {
    setRestoreState({
      isOpen: false,
      backup: null,
      confirmDbName: '',
      error: '',
    });
  }, []);

  const setRestoreConfirmDbName = useCallback((confirmDbName) => {
    setRestoreState((current) => ({
      ...current,
      confirmDbName,
      error: '',
    }));
  }, []);

  const submitRestore = useCallback(async () => {
    if (!restoreState.backup) {
      return;
    }

    setIsActionLoading(true);
    setError('');
    setSuccess('');
    setRestoreState((current) => ({ ...current, error: '' }));

    try {
      const response = await adminApi.restoreTenantBackup(token, {
        objectKey: restoreState.backup.objectKey,
        backupSetId: restoreState.backup.backupSetId,
        confirmDbName: restoreState.confirmDbName,
      });
      setSuccess(`Restore request queued: ${response?.runId || ''}`.trim());
      closeRestore();
      await load();
    } catch (requestError) {
      setRestoreState((current) => ({
        ...current,
        error: requestError?.message || 'Failed to queue restore request.',
      }));
    } finally {
      setIsActionLoading(false);
    }
  }, [token, restoreState.backup, restoreState.confirmDbName, closeRestore, load]);

  return {
    backups,
    runs,
    isLoading,
    isActionLoading,
    error,
    success,
    canRestore: hasPermission('backup.manage'),
    restoreState,
    refresh: load,
    downloadBackup,
    downloadBackupFiles,
    openRestore,
    closeRestore,
    setRestoreConfirmDbName,
    submitRestore,
  };
}
